import { useQuery } from '@tanstack/react-query';
import { Calculator, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type Fuel, listTemplatesSortBySchema, type TemplateSummary } from 'used-vehicle-contracts';
import { orpc } from '../../apis/orpc-query';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { InfoCell } from '../../components/ui/InfoCell';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { StatusText } from '../../components/ui/StatusText';
import { formatDay } from '../../lib/format';
import { useListSearchParams } from '../../lib/list-search-params';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { StarterPresetsCard } from './StarterPresetsCard';
import { TemplateSheet } from './TemplateSheet';

const FUELS: readonly Fuel[] = ['GAS', 'HYBRID', 'EV', 'DIESEL'];

/**
 * `/templates` (plan §7).
 *
 * A template is the **shared values** one model is priced with. The list says which version is live,
 * whether somebody is preparing a new one, and offers the action the page exists for: starting an
 * estimate from those values. Starting an estimate never creates a vehicle (plan §3.2).
 *
 * Page, order and filters are the service's answer and live in the URL, so the list is longer than one
 * page and a reload keeps its place (plan §11 fix 12).
 */
export const TemplatesListPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();

  const params = useListSearchParams();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const search = params.value('q');
  const fuelFilter = params.value('fuel');
  const includeArchived = params.value('includeArchived') === 'true';
  const sortBy = params.sortBy(listTemplatesSortBySchema.options, 'name');
  const sortDirection = params.sortDirection('asc');

  const listQuery = useQuery(
    orpc.templates.list.queryOptions({
      input: {
        page: params.page,
        pageSize: params.pageSize,
        sortBy,
        sortDirection,
        q: search.trim() === '' ? undefined : search.trim(),
        fuel: fuelFilter === '' ? undefined : (fuelFilter as Fuel),
        includeArchived,
      },
    }),
  );

  const columns = useMemo<DataTableColumns<TemplateSummary>>(
    () => [
      {
        id: 'name',
        header: t('templates.columnTemplate'),
        accessorFn: (row) => row.name,
        cell: ({ row }) => (
          <InfoCell
            title={row.original.name}
            subline={`${row.original.make} ${row.original.model} · ${t(`enums.fuel.${row.original.fuel}`)}${
              row.original.depreciationPreset ? ` · ${t(`templates.presets.${row.original.depreciationPreset}`)}` : ''
            }`}
          />
        ),
      },
      {
        id: 'publishedVersion',
        header: t('templates.columnPublished'),
        // The service sorts this list, and its `sortBy` enum offers no version or status order.
        enableSorting: false,
        accessorFn: (row) => row.publishedVersion ?? 0,
        cell: ({ row }) =>
          row.original.publishedVersion === null ? (
            <span className="text-sm text-muted-foreground">{t('templates.noPublishedVersion')}</span>
          ) : (
            <span
              dir="ltr"
              className="inline-block font-mono text-sm tabular-nums text-foreground"
            >
              v{row.original.publishedVersion}
            </span>
          ),
      },
      {
        id: 'draftVersion',
        header: t('templates.columnDraft'),
        enableSorting: false,
        cell: ({ row }) => (row.original.draftVersion === null ? null : <Badge tone="info">{t('templates.draftBadge')}</Badge>),
      },
      {
        id: 'status',
        header: t('templates.columnStatus'),
        enableSorting: false,
        accessorFn: (row) => row.status,
        cell: ({ row }) => <StatusText label={t(`templates.statuses.${row.original.status}`)} />,
      },
      {
        id: 'updatedAt',
        header: t('templates.columnUpdated'),
        accessorFn: (row) => row.updatedAt,
        cell: ({ row }) => <span className="text-sm text-muted-foreground">{formatDay(row.original.updatedAt)}</span>,
      },
      {
        id: 'actions',
        header: t('templates.columnActions'),
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            disabled={row.original.publishedVersion === null || row.original.status === 'ARCHIVED'}
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/estimates/new?templateId=${row.original.id}`);
            }}
          >
            <Calculator className="size-3.5" />
            {t('templates.startEstimate')}
          </Button>
        ),
      },
    ],
    [navigate, t],
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('templates.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('templates.subtitle')}</p>
        </div>
        <Button onClick={() => setIsSheetOpen(true)}>
          <Plus className="size-4" />
          {t('templates.create')}
        </Button>
      </header>

      <p className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">{t('templates.notice')}</p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <label
            htmlFor="templates-search"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('templates.search')}
          </label>
          <Input
            id="templates-search"
            value={search}
            placeholder={t('templates.searchPlaceholder')}
            onChange={(event) => params.update({ q: event.target.value })}
          />
        </div>
        <div className="w-44">
          <label
            htmlFor="templates-fuel-filter"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('templates.fuel')}
          </label>
          <Select
            id="templates-fuel-filter"
            value={fuelFilter}
            placeholder={t('common.all')}
            onChange={(event) => params.update({ fuel: event.target.value })}
            options={FUELS.map((fuel) => ({ value: fuel, label: t(`enums.fuel.${fuel}`) }))}
          />
        </div>
        <Checkbox
          id="templates-include-archived"
          label={t('templates.includeArchived')}
          checked={includeArchived}
          onChange={(event) => params.update({ includeArchived: event.target.checked ? 'true' : '' })}
        />
      </div>

      {listQuery.isError ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{t('errors.loadFailed')}</p>
      ) : null}

      <DataTable
        columns={columns}
        data={listQuery.data?.items ?? []}
        isLoading={listQuery.isLoading}
        emptyMessage={t('templates.empty')}
        pagination={{ page: params.page, pageSize: params.pageSize, total: listQuery.data?.meta.total ?? 0, onPageChange: params.setPage }}
        sorting={{ sortBy, sortDirection, onSortChange: params.setSort }}
        onRowClick={(row) => navigate(`/templates/${row.id}`)}
      />

      <StarterPresetsCard />

      <TemplateSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        onSaved={(saved) => navigate(`/templates/${saved.id}`)}
      />
    </div>
  );
};
